import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  performDocumentsMassAction,
  updateDocumentsTableConfig,
} from '../../../../../api'
import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../../constants/common'
import { SUBMENU_DEFAULT_VALUE } from '../../../../../constants/route'
import {
  useAsyncCallback,
  useModals,
  useNotification,
  useSelection,
  useUserData,
} from '../../../../../hooks'
import { getVisibleFilters } from '../../../../../utils/filter'
import {
  DocumentDraggableCellClone,
  DocumentFolderFormModal,
  getDocumentFolderFormErrorNotification,
  getDocumentFolderFormSuccessNotification,
  getDocumentsPermanentFilters,
  getDraggedItemsCount,
  getIdwellToIdwellUploadProps,
  useDocumentsTableActions,
  useDocumentsUploadFile,
} from '../../../../Document'
import { useFilePreviewGallery } from '../../../../FilesPreviewGallery'
import {
  useTableRefresh,
  useTableReloadAfterMassAction,
} from '../../../../Table'
import { getEntityTabDocumentsTableConfigApi } from '../../EntityTabDocuments.api'
import { ENTITY_TAB_DOCUMENTS_MODALS_CONFIG } from '../../EntityTabDocuments.constants'
import {
  getEntityTabDocumentsBreadcrumbs,
  getEntityTabDocumentsDownloadApiParams,
  getEntityTabDocumentsFilterBarApiUrl,
  getEntityTabDocumentsParent,
} from '../../EntityTabDocuments.utils'
import { useEntityTabDocumentsTable } from '../useEntityTabDocumentsTable'

export function useEntityTabDocumentsPage({
  breadcrumbRootItems,
  breadcrumbStorageItemId,
  contextData,
  entity,
  folderFormProps = EMPTY_OBJECT,
  getDownloadApiParams = getEntityTabDocumentsDownloadApiParams,
  getFilterBarApiUrl,
  getItemActions,
  getMassActions,
  isPreview,
  objectId,
  outboundUuid,
  renderUploadDestinationModal,
  rootDirectoryId,
  scope,
  sendViaEmailProps = EMPTY_OBJECT,
  sub = SUBMENU_DEFAULT_VALUE,
  uploadFileProps = EMPTY_OBJECT,
  onAfterUpload,
  onLoadCounters,
  onSendViaEmail,
}) {
  const { t } = useTranslation()

  const user = useUserData()

  const pageScrollWrapperRef = useRef(null)

  const [isConfigOpen, setConfigOpen] = useState(false)
  const [dndTargetId, setDndTargetId] = useState(null)

  const {
    closeEmailFormModal,
    closeModal,
    modals,
    setEmailFormModal,
    setModal,
  } = useModals(ENTITY_TAB_DOCUMENTS_MODALS_CONFIG)

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const {
    availableFilters,
    columns,
    filterValues,
    handleLoadInit,
    handleReload: handleReloadBase,
    handleResetOrdering,
    handleSetColumns,
    handleSetFilterValues,
    handleSetOrdering,
    handleSetPagination,
    handleSetSize,
    isInitialLoadSettled,
    isInitiated,
    isLoadingPage,
    isRefetching,
    items,
    ordering,
    pagination,
    requiredColumns,
    view,
  } = useEntityTabDocumentsTable({
    containerRef: pageScrollWrapperRef,
    entity,
    isPreview,
    objectId,
    outboundUuid,
    rootDirectoryId,
    scope,
    sub,
    tableConfigApi: getEntityTabDocumentsTableConfigApi({
      entity,
      outboundUuid,
    }),
  })

  const isLoading = !items || isLoadingPage

  async function handleReload() {
    return handleReloadBase().then(result => {
      onLoadCounters?.()

      return result
    })
  }

  const { refreshButtonProps } = useTableRefresh({
    isRefetching,
    onReload: handleReload,
  })

  useEffect(() => {
    if (objectId) {
      handleLoadInit()
    }
  }, [objectId])

  const {
    checkIsItemSelected,
    exclude,
    excludeIds,
    handleCleanSelection,
    handleExclude,
    handleSelect,
    handleSelectAll,
    handleUnselectAllPages,
    isAllSelected,
    isRemoveSelection,
    selectAllPages,
    selected,
    selectedIds,
  } = useSelection({ data: items, pagination })

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({
      files: items?.items,
    })

  async function performScopedMassAction(data) {
    return performDocumentsMassAction({
      data: {
        ...data,
        object_id: String(objectId),
        scope,
      },
      outboundUuid,
    })
  }

  const {
    handleChangeShouldReloadFilters,
    handleReloadTableAfterMassAction,
    shouldReloadFilters,
  } = useTableReloadAfterMassAction({
    filterValues,
    getPermanentFilters: getDocumentsPermanentFilters,
    sub,
    onReload: handleReload,
  })

  const parentId = filterValues?.parent || rootDirectoryId

  const {
    filesUploading,
    getRootDnDProps,
    getUploadFileInputProps,
    handleClickUploadFile,
    hoveredId,
  } = useDocumentsUploadFile({
    ...getIdwellToIdwellUploadProps(outboundUuid),
    ...uploadFileProps,
    outboundUuid,
    parent: parentId,
    renderUploadDestinationModal,
    onAfterUpload,
    onCloseModal: closeModal,
    onReload: handleReloadTableAfterMassAction,
    onSetModal: setModal,
  })

  const { handleDragDocumentEnd, selectedFiles, setItemAction, setMassAction } =
    useDocumentsTableActions({
      downloadApiParams: getDownloadApiParams({ objectId, scope }),
      exclude,
      excludeIds,
      filterValues: {
        ...filterValues,
        parent: getEntityTabDocumentsParent({ filterValues, rootDirectoryId }),
        sub_menu: sub,
      },
      items: items?.items,
      outboundUuid,
      pagination,
      parent: parentId,
      performMassActionApi: performScopedMassAction,
      requestId: objectId,
      selectAllPages,
      selected,
      selectedIds,
      sendViaEmailProps,
      sub,
      unselectAllPages: handleUnselectAllPages,
      onCleanSelection: handleCleanSelection,
      onCloseEmailFormModal: closeEmailFormModal,
      onCloseModal: closeModal,
      onReload: handleReloadTableAfterMassAction,
      onSendViaEmail,
      onSetEmailFormModal: setEmailFormModal,
      onSetModal: setModal,
    })

  function handleDragOver(event) {
    const overId = event?.over?.id

    if (!overId) {
      setDndTargetId(null)

      return
    }

    const overItem = items?.items?.find(i => String(i.id) === String(overId))

    if (overItem?.permissions?.can_write) {
      setDndTargetId(overItem.id)

      return
    }

    setDndTargetId(null)
  }

  function handleDragEnd(event) {
    setDndTargetId(null)
    handleDragDocumentEnd(event)
  }

  function renderOverlay(activeId, activeData) {
    const item = activeData?.item

    if (!item) {
      return null
    }

    const count = getDraggedItemsCount({
      activeId,
      excludeIds,
      pagination,
      selectAllPages,
      selectedIds,
    })

    return <DocumentDraggableCellClone count={count} item={item} />
  }

  const massActions = getMassActions({
    contextData,
    permissions: items?.permissions || EMPTY_OBJECT,
    selectAllPages,
    selectedFiles,
    t,
    user,
  })

  function getRowItemActions(item) {
    return getItemActions({
      commonPermissions: items?.permissions,
      contextData,
      item,
      user,
    })
  }

  function handleSetParent(nextParent) {
    const nextValue = nextParent == null ? rootDirectoryId : nextParent

    handleSetFilterValues({
      ...(filterValues || EMPTY_OBJECT),
      parent: nextValue ? String(nextValue) : undefined,
    })

    if (pagination?.page && pagination.page !== 1) {
      handleSetPagination({ ...pagination, page: 1 })
    }
  }

  function handleClickFolder(item) {
    handleSetParent(item?.id)
  }

  function handleSuccessCreation(data) {
    closeModal()

    const notification = getDocumentFolderFormSuccessNotification(data, { t })

    handleAddSuccessNotification({
      ...notification,
      linkProps: null,
      textButtonValue: notification?.linkProps?.title,
      onClickTextButton: () => handleSetParent(data?.id),
    })

    handleReload()
  }

  function handleErrorCreation() {
    handleAddErrorNotification(getDocumentFolderFormErrorNotification(t))
  }

  function handleOpenCreation() {
    setModal(
      <DocumentFolderFormModal
        {...folderFormProps}
        outboundUuid={outboundUuid}
        parent={parentId}
        onClose={closeModal}
        onError={handleErrorCreation}
        onSuccess={handleSuccessCreation}
      />
    )
  }

  const breadcrumbs =
    breadcrumbRootItems == null
      ? null
      : getEntityTabDocumentsBreadcrumbs({
          breadcrumbRootItems,
          breadcrumbStorageItemId,
          handleSetParent,
          items,
          rootDirectoryId,
          t,
        })

  const handleUpdateConfig = useAsyncCallback(
    obj => {
      return updateDocumentsTableConfig(
        {
          columns,
          view,
          ...obj,
        },
        entity.plural
      )
    },
    [columns, entity.plural, view]
  )

  function handleConfigOpen() {
    setConfigOpen(true)
  }

  const tableItems = [...filesUploading, ...(items?.items || EMPTY_LIST)]

  const showFilterBar =
    getVisibleFilters(availableFilters).length > 0 || isLoading

  const showDocumentActions =
    items?.parent?.permissions?.can_create || isLoading

  const filterBarApiUrl = getEntityTabDocumentsFilterBarApiUrl({
    filterValues,
    getFilterBarApiUrl,
    objectId,
    outboundUuid,
    rootDirectoryId,
    scope,
    sub,
  })

  const hoveredRowId = dndTargetId ?? hoveredId

  return {
    activeFileIndex,
    availableFilters,
    breadcrumbs,
    checkIsItemSelected,
    columns,
    exclude,
    filterBarApiUrl,
    filterValues,
    getRootDnDProps,
    getRowItemActions,
    getUploadFileInputProps,
    handleChangeShouldReloadFilters,
    handleClickFile,
    handleClickFolder,
    handleClickUploadFile,
    handleCloseFilePreview,
    handleConfigOpen,
    handleDragEnd,
    handleDragOver,
    handleExclude,
    handleOpenCreation,
    handleReload,
    handleReloadList: handleReloadBase,
    handleResetOrdering,
    handleSelect,
    handleSelectAll,
    handleSetColumns,
    handleSetFilterValues,
    handleSetOrdering,
    handleSetPagination,
    handleSetSize,
    handleUpdateConfig,
    hoveredRowId,
    isAllSelected,
    isConfigOpen,
    isInitialLoadSettled,
    isInitiated,
    isLoading,
    isRemoveSelection,
    items,
    massActions,
    modals,
    ordering,
    pageScrollWrapperRef,
    pagination,
    refreshButtonProps,
    renderOverlay,
    requiredColumns,
    scope,
    selectAllPages,
    selected,
    setConfigOpen,
    setItemAction,
    setMassAction,
    shouldReloadFilters,
    showDocumentActions,
    showFilterBar,
    sub,
    tableItems,
  }
}
